"use client";
import { useAlgoliaSearchClient } from "@lib/wrappers/algolia/algoliaSearchClient";
import type { AlgoliaSearchClient } from "@lib/wrappers/algolia/types";
import type { SearchResponse } from "algoliasearch/lite";
import { useLocale } from "next-intl";
import { useEffect, useState } from "react";

export type DidYouMeanSuggestion = {
    term: string;
    count?: number;
};

type QuerySuggestionHit = {
    objectID: string;
    query?: string;
    popularity?: number;
    [key: string]: unknown;
};

function getExactNbHits(hit: QuerySuggestionHit, indexName: string): number | undefined {
    const nested = hit[indexName];
    if (nested && typeof nested === "object" && "exact_nb_hits" in nested) {
        const value = (nested as { exact_nb_hits?: unknown }).exact_nb_hits;
        if (typeof value === "number") {
            return value;
        }
    }

    return undefined;
}

async function getProductsIndexCount(
    searchClient: AlgoliaSearchClient,
    indexName: string,
    term: string
): Promise<number | undefined> {
    try {
        // The lite client (algoliaSearchClient.ts) doesn't have searchSingleIndex, so we use its
        // generic search() with one request instead. This is a hits request rather than a
        // facet-values one, so the result has `nbHits` on it.
        const [result] = (
            await searchClient.search({
                requests: [
                    {
                        indexName,
                        query: term,
                        hitsPerPage: 0,
                        facetFilters: ["hasStock:true"],
                        clickAnalytics: false,
                        analytics: false,
                    },
                ],
            })
        ).results;
        const { nbHits } = result as SearchResponse;

        return typeof nbHits === "number" ? nbHits : undefined;
    } catch {
        return undefined;
    }
}

/**
 * Fetches the top Algolia query suggestion for a failed search query.
 * Uses the same `_query_suggestions` index as the search bar, via a direct
 * search (avoids autocomplete lifecycle issues on the empty-results page).
 */
export function useDidYouMeanSuggestion(query: string): DidYouMeanSuggestion | null {
    const locale = useLocale();
    const { searchClient, indexName } = useAlgoliaSearchClient(locale);
    const [suggestion, setSuggestion] = useState<DidYouMeanSuggestion | null>(null);

    useEffect(() => {
        const trimmedQuery = query?.trim();
        if (!trimmedQuery) {
            setSuggestion(null);
            return;
        }

        let cancelled = false;

        const fetchSuggestion = async () => {
            try {
                // Same as above: the lite client doesn't have searchSingleIndex, so we fall
                // back to search() with a single request.
                const [{ hits }] = (
                    await searchClient.search<QuerySuggestionHit>({
                        requests: [
                            {
                                indexName: `${indexName}_query_suggestions`,
                                query: trimmedQuery,
                                hitsPerPage: 5,
                            },
                        ],
                    })
                ).results as SearchResponse<QuerySuggestionHit>[];

                if (cancelled) {
                    return;
                }

                const match = hits
                    .filter(
                        (hit) => hit.query && hit.query.toLowerCase() !== trimmedQuery.toLowerCase()
                    )
                    .reduce<QuerySuggestionHit | null>((best, hit) => {
                        if (!best) return hit;
                        return (hit.popularity ?? 0) > (best.popularity ?? 0) ? hit : best;
                    }, null);

                if (!match?.query) {
                    setSuggestion(null);
                    return;
                }

                const productsCount = await getProductsIndexCount(
                    searchClient,
                    indexName,
                    match.query
                );

                if (cancelled) {
                    return;
                }

                setSuggestion({
                    term: match.query,
                    // Prefer a live count from products index (with stock filter),
                    // fallback to query-suggestions metadata if available.
                    count: productsCount ?? getExactNbHits(match, indexName),
                });
            } catch {
                if (!cancelled) {
                    setSuggestion(null);
                }
            }
        };

        void fetchSuggestion();

        return () => {
            cancelled = true;
        };
    }, [searchClient, indexName, query]);

    return suggestion;
}
